import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { signIn, signOut, getCurrentUser, fetchAuthSession, confirmSignIn, signInWithRedirect } from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { User, BusinessUnit, AppError, Authorization } from '@/types';
import config, { amplifyConfig } from '@/utils/config';
import { apiService } from '@/utils/api';
import { extractCognitoErrorMessage } from '@/utils/cognitoErrors';
import { ALL_BUSINESS_UNITS_ID, ALL_BUSINESS_UNITS_LABEL } from '@/utils/businessUnits';

/**
 * Validates if an email address belongs to a service account that should not be allowed to login
 * Rejects emails matching patterns like: *-ad@TNC.ORG, *-aws@TNC.ORG, *-hr@TNC.ORG
 */
function isServiceAccountEmail(email: string): boolean {
  if (!email) return false;
  
  // Normalize email to lowercase for comparison
  const normalizedEmail = email.toLowerCase().trim();
  
  // Check for service account patterns (case-insensitive domain matching)
  // Patterns: *-ad@tnc.org, *-aws@tnc.org, *-hr@tnc.org, *-az@tnc.org
  const serviceAccountPatterns = [
    /^.+?-ad@tnc\.org$/i,
    /^.+?-aws@tnc\.org$/i,
    /^.+?-hr@tnc\.org$/i,
    /^.+?-az@tnc\.org$/i,
  ];
  
  return serviceAccountPatterns.some(pattern => pattern.test(normalizedEmail));
}

function resolveDefaultBusinessUnit(
  units: BusinessUnit[],
  persisted: BusinessUnit | null
): BusinessUnit | null {
  if (persisted) {
    const stillAccessible = units.find((bu) => bu.id === persisted.id);
    if (stillAccessible) {
      return stillAccessible;
    }
  }
  return units[0] ?? null;
}

/** Display only. Access decisions use the canonical id, never this label. */
const UNIT_LABELS: Record<string, string> = {
  CA: 'CA',
  MEM: 'MEM',
  CI360: 'CI360',
  DataServices: 'Data Services',
};

/** /identity returns ids only. Known catalog ids get a label; anything else stays the id. */
export function businessUnitLabel(id: string): string {
  return UNIT_LABELS[id] ?? id;
}

function businessUnitForId(id: string): BusinessUnit {
  return {
    id,
    name: businessUnitLabel(id),
    dataClassification: 'internal',
    costCenter: '',
    retentionDays: 0,
    users: [],
  };
}

/**
 * Business units the caller may select. A global admin gets All first, then
 * every catalog id. Everyone else gets the units they hold that appear in
 * the catalog. CI360 is included. provisioned does not decide membership.
 */
export function selectableBusinessUnits(authorization: Authorization | null): BusinessUnit[] {
  if (!authorization) {
    return [];
  }
  const catalogIds = authorization.catalog.map((entry) => entry.id);
  if (authorization.globalAdmin) {
    const all = businessUnitForId(ALL_BUSINESS_UNITS_ID);
    all.name = ALL_BUSINESS_UNITS_LABEL;
    return [all, ...catalogIds.map(businessUnitForId)];
  }
  const ids = authorization.units
    .map((unit) => unit.id)
    .filter((id) => catalogIds.includes(id));
  return ids.map(businessUnitForId);
}

/**
 * One /identity result per page load. Cleared on logout (and by a full
 * reload, which re-inits this module). login and confirmPasswordChange
 * pass force so a new session always refetches.
 */
let identitySettledThisSession = false;

export type AuthorizationStatus = 'pending' | 'ready' | 'unavailable';

type IdentityLoad =
  | { status: 'ready'; authorization: Authorization }
  | { status: 'unavailable' }
  | { status: 'unauthenticated' };

function sessionExpiredError(): Error {
  const error = new Error('Your session expired. Sign in again.');
  (error as { code?: string }).code = 'SESSION_EXPIRED';
  return error;
}

async function loadAuthorization(force = false): Promise<IdentityLoad> {
  if (!force && identitySettledThisSession) {
    const state = useAuthStore.getState();
    if (state.authorizationStatus === 'ready' && state.authorization) {
      return { status: 'ready', authorization: state.authorization };
    }
    if (state.authorizationStatus === 'unavailable') {
      return { status: 'unavailable' };
    }
  }
  try {
    const authorization = await apiService.getIdentity();
    identitySettledThisSession = true;
    return { status: 'ready', authorization };
  } catch (error) {
    const code = (error as { code?: string }).code;
    if (code === 'IDENTITY_UNAUTHORIZED') {
      identitySettledThisSession = false;
      return { status: 'unauthenticated' };
    }
    identitySettledThisSession = true;
    console.error('GET /identity failed; authorization left unset', error);
    return { status: 'unavailable' };
  }
}

function clearIdentitySession() {
  identitySettledThisSession = false;
}

interface PasswordResetState {
  isRequired: boolean;
  username: string;
}

interface AuthState {
  user: User | null;
  /** Resolved by GET /identity. Gates and business-unit selection read this. */
  authorization: Authorization | null;
  /** pending until GET /identity settles; unavailable is a load failure, not a denial. */
  authorizationStatus: AuthorizationStatus;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AppError | null;
  currentBusinessUnit: BusinessUnit | null;
  passwordResetState: PasswordResetState | null;
  
  // Actions
  login: (username: string, password: string) => Promise<void>;
  loginWithSSO: () => Promise<void>;
  loginWithAzureAD: (username: string, password: string) => Promise<void>;
  confirmPasswordChange: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  retryIdentity: () => Promise<void>;
  setCurrentBusinessUnit: (businessUnitId: string) => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  clearPasswordResetState: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      authorization: null,
      authorizationStatus: 'pending',
      isAuthenticated: false,
      isLoading: true,
      error: null,
      currentBusinessUnit: null,
      passwordResetState: null,

      login: async (username: string, password: string) => {
        try {
          set({ isLoading: true, error: null });
          
          // Verify Amplify is configured
          if (!amplifyConfig.Auth?.Cognito?.userPoolId || !amplifyConfig.Auth?.Cognito?.userPoolClientId) {
            throw new Error('AWS Cognito is not configured. Please check your environment variables.');
          }
          
          // Check if there's already a signed in user and sign them out
          // This prevents "There is already a signed in user" errors after failed logins
          try {
            await getCurrentUser();
            console.log('Found existing signed in user, signing out before new login attempt');
            await signOut();
            // Give it a moment to complete
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            // No existing user or sign out failed - that's okay, continue with login
            console.log('No existing user to sign out:', err);
          }
          
          // Sign in and check the result
          const signInResult = await signIn({ username, password });
          
          // Handle additional steps from Cognito sign-in flow
          if (signInResult.nextStep) {
            const step = signInResult.nextStep.signInStep;
            
            // Check if password reset is required
            if (step === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
              console.log('Password reset required for user:', username);
              set({
                isLoading: false,
                passwordResetState: {
                  isRequired: true,
                  username: username,
                }
              });
              const err = new Error('Password change required. Please set a new password.');
              (err as any).code = 'PASSWORD_CHANGE_REQUIRED';
              throw err;
            }
            
            // DONE means sign-in is complete; proceed normally
            if (step && step !== 'DONE') {
              console.log('Sign-in requires additional steps:', signInResult.nextStep);
              const friendly = typeof step === 'string' ? step : 'additional_step_required';
              const err = new Error(`Additional authentication required: ${friendly}`);
              (err as any).code = 'ADDITIONAL_AUTH_REQUIRED';
              throw err;
            }
          }
          
          // Verify user is actually signed in
          let cognitoUser;
          let attempts = 0;
          while (attempts < 5) {
            try {
              cognitoUser = await getCurrentUser();
              if (cognitoUser) break;
            } catch (error) {
              console.log(`Attempt ${attempts + 1}: User not yet available, waiting...`);
            }
            await new Promise(resolve => setTimeout(resolve, 200));
            attempts++;
          }
          
          if (!cognitoUser) {
            throw new Error('Failed to verify user authentication. Please try again.');
          }
          
          // Now fetch the session - user should be authenticated
          let session = await fetchAuthSession({ forceRefresh: true });
          
          // If still no tokens, try a few more times
          attempts = 0;
          while (!session.tokens && attempts < 3) {
            await new Promise(resolve => setTimeout(resolve, 300));
            session = await fetchAuthSession({ forceRefresh: true });
            attempts++;
          }
          
          if (!session.tokens) {
            console.error('Session details:', { 
              hasSession: !!session, 
              hasTokens: !!session.tokens,
              user: cognitoUser?.username 
            });
            throw new Error('Authentication succeeded but tokens are unavailable. Please check your AWS Cognito configuration.');
          }
          
          const attributes = (session.tokens.idToken?.payload || {}) as Record<string, unknown>;
          
          // Extract user information from ID token attributes
          const email = (attributes.email as string) || '';
          // Note: Azure AD does NOT provide email_verified claim in ID tokens
          // For federated Azure AD users, assume email is verified (managed by AD admins)
          // If a Lambda trigger added it, use that value; otherwise default to true
          const emailVerified = attributes.email_verified as boolean | undefined ?? true;
          const name = (attributes.name as string) || '';
          const givenName = (attributes.given_name as string) || '';
          const familyName = (attributes.family_name as string) || '';
          
          // Log email verification status for debugging
          if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
            console.log('User email verification status:', {
              email,
              emailVerified,
              emailVerifiedInToken: attributes.email_verified !== undefined,
              note: attributes.email_verified === undefined 
                ? 'email_verified claim not in token (Azure AD limitation) - defaulting to true for federated users'
                : 'email_verified claim present (likely from Cognito Lambda trigger)'
            });
          }
          
          // Validate email - reject service account emails
          if (email && isServiceAccountEmail(email)) {
            console.warn(`Service account email detected and rejected: ${email}`);
            await signOut(); // Sign out the service account
            throw new Error('Service account logins are not permitted. Please use your standard TNC login email address.');
          }

          const loaded = await loadAuthorization(true);
          if (loaded.status === 'unauthenticated') {
            throw sessionExpiredError();
          }
          const authorization = loaded.status === 'ready' ? loaded.authorization : null;
          const authorizationStatus = loaded.status;
          
          // Create display name: prefer name, then givenName + familyName, then email, finally username
          const displayName = name || 
            (givenName && familyName ? `${givenName} ${familyName}` : givenName || familyName) ||
            email ||
            username;
          
          const defaultBusinessUnit = resolveDefaultBusinessUnit(
            selectableBusinessUnits(authorization),
            get().currentBusinessUnit
          );

          const user: User = {
            id: username,
            username: username, // Cognito username (hash for federated users)
            email: email || username, // Email address (preferred for display)
            displayName: displayName, // Human-readable display name
            businessUnit: defaultBusinessUnit,
            clearanceLevel: 'standard',
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
          };
          
          // Log successful authentication for debugging
          console.log('Login successful:', {
            username: user.username,
            email: user.email,
            displayName: user.displayName,
            hasAccessToken: !!session.tokens.accessToken,
            hasIdToken: !!session.tokens.idToken,
            selectedBusinessUnit: defaultBusinessUnit?.name ?? null
          });
          
          set({
            user,
            authorization,
            authorizationStatus,
            isAuthenticated: true,
            currentBusinessUnit: defaultBusinessUnit,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          const appError: AppError = {
            code: (error as { code?: string })?.code || 'AUTH_ERROR',
            message: (error as { message?: string })?.message || 'Authentication failed',
            details: error,
            timestamp: new Date().toISOString(),
          };
          
          // If login failed, make sure to clean up any partial sign-in state
          try {
            const currentUser = await getCurrentUser();
            if (currentUser) {
              console.log('Login failed but user is signed in, signing out to clean up state');
              await signOut();
            }
          } catch (signOutError) {
            // Ignore sign out errors during cleanup
            console.log('Sign out during login error cleanup failed (this is usually okay):', signOutError);
          }
          
          set({
            error: appError,
            isLoading: false,
            isAuthenticated: false,
            user: null,
            authorization: null,
          });
          throw appError;
        }
      },

      loginWithSSO: async () => {
        // Clear any existing errors before starting SSO
        set({ isLoading: true, error: null });
        
        try {
          // Verify Amplify is configured with Cognito domain
          if (!amplifyConfig.Auth?.Cognito?.userPoolId || !amplifyConfig.Auth?.Cognito?.userPoolClientId) {
            throw new Error('AWS Cognito is not configured. Please check your environment variables.');
          }
          
          // Verify OAuth configuration is correct
          if (!config.cognitoDomain) {
            const errorMsg = 'Cognito domain is not configured. Please set VITE_COGNITO_DOMAIN in your .env.local file.';
            console.error(errorMsg);
            console.error('Environment variable VITE_COGNITO_DOMAIN:', import.meta.env.VITE_COGNITO_DOMAIN);
            throw new Error(errorMsg);
          }
          
          // Check if OAuth config exists in amplifyConfig
          // IMPORTANT: In AWS Amplify v6, OAuth config is under Auth.Cognito.loginWith.oauth
          const cognitoConfig: any = amplifyConfig.Auth.Cognito;
          const oauthConfig = cognitoConfig?.loginWith?.oauth;
          
          if (!oauthConfig) {
            const errorMsg = 'OAuth is not configured in Amplify config. Please check your configuration.';
            console.error(errorMsg);
            console.error('Expected location: Auth.Cognito.loginWith.oauth');
            console.error('amplifyConfig.Auth.Cognito:', cognitoConfig);
            console.error('config.cognitoDomain:', config.cognitoDomain);
            console.error('VITE_COGNITO_DOMAIN:', import.meta.env.VITE_COGNITO_DOMAIN);
            throw new Error(errorMsg);
          }
          
          // CRITICAL: Rebuild Amplify config with current origin to avoid InvalidOriginException
          // InvalidOriginException occurs when redirect URLs don't match the current origin
          // This is a security feature - Amplify requires redirect URLs to match where the app is running
          // We must rebuild the config with the current origin at runtime, not use the one built at module load
          const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
          
          if (!currentOrigin) {
            throw new Error('Cannot determine current origin. OAuth configuration requires browser context.');
          }
          
          // Rebuild OAuth config with current origin to ensure it matches
          // This prevents InvalidOriginException which occurs when redirect URLs don't match current origin
          // IMPORTANT: Create a NEW object instead of modifying the existing one (which may be read-only)
          const correctedRedirectSignIn = `${currentOrigin}/oauth2/callback`;
          const correctedRedirectSignOut = `${currentOrigin}/login`;
          
          // Create a new OAuth config object with current origin (don't modify the existing one)
          // IMPORTANT: Copy values individually to avoid any read-only property issues
          // Use mutable arrays (not readonly) to match Amplify's expected types
          const correctedOAuthConfig = {
            domain: config.cognitoDomain, // Use config directly instead of oauthConfig.domain
            scopes: ['email', 'openid', 'profile'] as string[], // Mutable array, not readonly
            redirectSignIn: [correctedRedirectSignIn],
            redirectSignOut: [correctedRedirectSignOut],
            responseType: 'code' as const,
          };
          
          // Create a new Amplify config with corrected OAuth config
          // IMPORTANT: In production, Amplify may not properly merge configs
          // We need to create a completely fresh config object with all required properties
          // Do NOT spread from amplifyConfig as it may contain read-only or frozen properties
          const correctedAmplifyConfig = {
            Auth: {
              Cognito: {
                userPoolId: config.userPoolId,
                userPoolClientId: config.userPoolClientId,
                identityPoolId: config.identityPoolId,
                loginWith: {
                  email: true,
                  username: true,
                  oauth: correctedOAuthConfig, // Use corrected OAuth config with current origin
                },
              },
            },
            Storage: amplifyConfig.Storage ? { ...amplifyConfig.Storage } : undefined,
            API: amplifyConfig.API ? { ...amplifyConfig.API } : undefined,
          } as any; // Use 'as any' to bypass TypeScript strict checking since we're creating a runtime config
          
          // Remove undefined properties to avoid issues
          if (!correctedAmplifyConfig.Storage) {
            delete correctedAmplifyConfig.Storage;
          }
          if (!correctedAmplifyConfig.API) {
            delete correctedAmplifyConfig.API;
          }
          
          // Only log detailed config in development
          if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
            console.log('Configuring Amplify with OAuth config (using current origin):', {
              domain: correctedOAuthConfig.domain,
              redirectSignIn: correctedOAuthConfig.redirectSignIn,
              redirectSignOut: correctedOAuthConfig.redirectSignOut,
              scopes: correctedOAuthConfig.scopes,
              responseType: correctedOAuthConfig.responseType,
              currentOrigin: currentOrigin,
              environment: import.meta.env.MODE,
              note: 'Redirect URLs are set to current origin to avoid InvalidOriginException',
            });
            console.log('⚠️ IMPORTANT: Ensure Cognito has this redirect URL registered:', correctedRedirectSignIn);
            console.log('   If not registered, you will get a redirect_mismatch error from Cognito.');
          }
          
          // Configure Amplify with corrected config (new object, not modified)
          // Log config structure only in development or if explicitly enabled
          if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
            console.log('🔧 Configuring Amplify with corrected config structure:', {
              hasAuth: !!correctedAmplifyConfig.Auth,
              hasCognito: !!correctedAmplifyConfig.Auth?.Cognito,
              hasLoginWith: !!correctedAmplifyConfig.Auth?.Cognito?.loginWith,
              hasOAuth: !!correctedAmplifyConfig.Auth?.Cognito?.loginWith?.oauth,
              oauthConfig: correctedAmplifyConfig.Auth?.Cognito?.loginWith?.oauth,
              currentOrigin: currentOrigin,
            });
          }
          
          // CRITICAL: In production, Amplify may cache the previous configuration
          // We need to ensure the config is completely replaced, not merged
          // Configure Amplify with the corrected config
          // This should completely replace any existing configuration
          Amplify.configure(correctedAmplifyConfig);
          
          // Wait longer for Amplify to process the configuration in production
          // Production builds may need more time to process the config
          // CRITICAL: Give Amplify enough time to fully process the OAuth configuration
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Verify configuration was applied (check under Cognito.loginWith.oauth)
          // IMPORTANT: Get config multiple times to ensure it's stable
          let currentConfig: any;
          let currentOAuthConfig: any;
          
          // Try getting config multiple times to ensure it's applied
          for (let i = 0; i < 3; i++) {
            currentConfig = Amplify.getConfig();
            currentOAuthConfig = currentConfig.Auth?.Cognito?.loginWith?.oauth;
            
            if (currentOAuthConfig) {
              if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
                console.log(`✅ OAuth config found on attempt ${i + 1}`);
              }
              break;
            } else if (i < 2) {
              // Only log warnings in dev mode, but always wait for retry
              if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
                console.warn(`⚠️ OAuth config not found on attempt ${i + 1}, waiting...`);
              }
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          }
          
          // Log detailed config state only in development
          if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
            console.log('📊 Amplify config after configure:', {
              hasAuth: !!currentConfig.Auth,
              hasCognito: !!currentConfig.Auth?.Cognito,
              hasLoginWith: !!currentConfig.Auth?.Cognito?.loginWith,
              hasOAuth: !!currentConfig.Auth?.Cognito?.loginWith?.oauth,
              oauthConfig: currentConfig.Auth?.Cognito?.loginWith?.oauth,
            });
          }
          
          if (!currentOAuthConfig) {
            console.error('❌ OAuth configuration not found after Amplify.configure');
            console.error('Expected location: Auth.Cognito.loginWith.oauth');
            console.error('Current config structure:', {
              hasAuth: !!currentConfig.Auth,
              hasCognito: !!currentConfig.Auth?.Cognito,
              hasLoginWith: !!currentConfig.Auth?.Cognito?.loginWith,
              cognitoConfig: currentConfig.Auth?.Cognito,
              loginWith: currentConfig.Auth?.Cognito?.loginWith,
              fullConfig: JSON.stringify(currentConfig, null, 2),
            });
            console.error('Corrected OAuth config that was passed:', JSON.stringify(correctedOAuthConfig, null, 2));
            console.error('Corrected Amplify config structure:', JSON.stringify(correctedAmplifyConfig, null, 2));
            console.error('Environment:', {
              mode: import.meta.env.MODE,
              isDev: import.meta.env.DEV,
              cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN,
              redirectSignIn: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN,
              redirectSignOut: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT,
              cloudFrontUrl: import.meta.env.VITE_CLOUDFRONT_URL,
              currentOrigin: currentOrigin,
            });
            
            // In production, if OAuth config isn't found, try one more time with a longer wait
            console.warn('⚠️ OAuth config not found, waiting longer and trying again...');
            await new Promise(resolve => setTimeout(resolve, 1000));
            const retryConfig: any = Amplify.getConfig();
            const retryOAuthConfig = retryConfig.Auth?.Cognito?.loginWith?.oauth;
            
            if (!retryOAuthConfig) {
              console.error('❌ OAuth config still not found after retry');
              throw new Error('Failed to configure OAuth in Amplify. The OAuth config may not be in the correct format. Please check your VITE_COGNITO_DOMAIN environment variable.');
            } else {
              console.log('✓ OAuth config found after retry');
            }
          }
          
          // Final validation: ensure redirect URLs match current origin
          const finalOAuthConfig = currentOAuthConfig || (Amplify.getConfig() as any).Auth?.Cognito?.loginWith?.oauth;
          const finalRedirectSignIn = finalOAuthConfig?.redirectSignIn?.[0] || '';
          if (currentOrigin && finalRedirectSignIn) {
            try {
              const finalRedirectUrl = new URL(finalRedirectSignIn);
              if (finalRedirectUrl.origin !== currentOrigin) {
                console.error('FINAL VALIDATION FAILED: Redirect URL origin still doesn\'t match current origin!');
                console.error('Current origin:', currentOrigin);
                console.error('Redirect URL origin:', finalRedirectUrl.origin);
                console.error('This will cause InvalidOriginException. Please check your configuration.');
              } else {
                // Only log success in development
                if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
                  console.log('✓ Redirect URL origin matches current origin');
                }
              }
            } catch (urlError) {
              // Always log errors
              console.error('Error validating final redirect URL:', urlError);
            }
          }
          
          // Only log verification in development
          if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
            console.log('✓ OAuth configuration verified in Amplify:', {
              domain: finalOAuthConfig?.domain,
              redirectSignIn: finalOAuthConfig?.redirectSignIn,
              redirectSignOut: finalOAuthConfig?.redirectSignOut,
              scopes: finalOAuthConfig?.scopes,
              responseType: finalOAuthConfig?.responseType,
              currentOrigin: currentOrigin,
            });
          }
          
          // Redirect to Cognito Hosted UI for OIDC SSO (Azure AD)
          // Prioritize OIDC provider over SAML (backend uses OIDC with Azure AD)
          const providerName = config.cognitoOidcProvider || config.cognitoIdpProvider || config.cognitoSamlProvider;
          
          // Pre-sign-out to clear any stale sessions
          try {
            await signOut();
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            // Ignore sign-out errors - proceed with SSO
          }
          
          // ALWAYS use signInWithRedirect - never use manual URL construction
          // Manual URL construction breaks Amplify's OAuth state tracking
          // Note: signInWithRedirect should redirect the browser immediately.
          // If it returns without throwing, the redirect is in progress.
          // We don't set isLoading to false here because the redirect should happen.
          try {
            if (providerName) {
              if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
                console.log('Calling signInWithRedirect with provider:', providerName);
              }
              await signInWithRedirect({ 
                provider: { 
                  custom: providerName 
                } as any 
              });
            } else {
              if (import.meta.env.DEV || import.meta.env.VITE_DEBUG_CONFIG) {
                console.log('Calling signInWithRedirect without provider');
              }
              await signInWithRedirect();
            }
          } catch (redirectError: any) {
            // Log the error but check if it's an OAuth configuration error
            console.error('signInWithRedirect error:', redirectError);
            console.error('Error details:', {
              name: redirectError?.name,
              message: redirectError?.message,
              code: redirectError?.code,
              underlyingError: redirectError?.underlyingError,
              recoverySuggestion: redirectError?.recoverySuggestion,
            });
            
            // Check for InvalidOriginException - this happens when redirect URLs don't match current origin
            if (redirectError?.name === 'InvalidOriginException' || 
                redirectError?.message?.includes('different origin') ||
                redirectError?.message?.includes('same origin')) {
              const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'SSR';
              console.error('InvalidOriginException detected - redirect URLs must match current origin');
              console.error('Origin mismatch details:', {
                currentOrigin: currentOrigin,
                redirectSignIn: currentOAuthConfig?.redirectSignIn,
                redirectSignOut: currentOAuthConfig?.redirectSignOut,
                redirectSignInOrigin: currentOAuthConfig?.redirectSignIn ? new URL(currentOAuthConfig.redirectSignIn[0]).origin : 'N/A',
                redirectSignOutOrigin: currentOAuthConfig?.redirectSignOut ? new URL(currentOAuthConfig.redirectSignOut[0]).origin : 'N/A',
                matches: currentOAuthConfig?.redirectSignIn?.[0]?.startsWith(currentOrigin) || false,
              });
              console.error('SOLUTION: Ensure redirect URLs match current origin. The app is configured to use current origin automatically.');
              console.error('Make sure Cognito has the redirect URL registered:', `${currentOrigin}/oauth2/callback`);
              
              // Re-throw with a clearer error message
              throw new Error(`OAuth redirect URLs must match current origin. Current origin: ${currentOrigin}, but redirect URL is: ${currentOAuthConfig?.redirectSignIn?.[0]}. Please ensure Cognito has ${currentOrigin}/oauth2/callback registered as a callback URL.`);
            }
            
            // If it's an OAuth configuration error, log the current config state
            if (redirectError?.message?.includes('OAuth') || 
                redirectError?.message?.includes('not configured') ||
                redirectError?.name === 'OAuthNotConfigureException') {
              console.error('OAuth configuration error detected. Current state:');
              console.error('Current OAuth config in Amplify:', currentOAuthConfig);
              console.error('Amplify config structure:', {
                hasAuth: !!currentConfig.Auth,
                hasCognito: !!currentConfig.Auth?.Cognito,
                hasLoginWith: !!currentConfig.Auth?.Cognito?.loginWith,
                hasOAuth: !!currentConfig.Auth?.Cognito?.loginWith?.oauth,
                cognitoConfig: currentConfig.Auth?.Cognito,
              });
              console.error('Redirect URLs:', {
                redirectSignIn: currentOAuthConfig?.redirectSignIn,
                redirectSignOut: currentOAuthConfig?.redirectSignOut,
                currentOrigin: typeof window !== 'undefined' ? window.location.origin : 'SSR',
                cloudFrontUrl: import.meta.env.VITE_CLOUDFRONT_URL,
                explicitRedirectSignIn: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_IN,
                explicitRedirectSignOut: import.meta.env.VITE_COGNITO_REDIRECT_SIGN_OUT,
              });
              
              // Re-throw the error so it can be handled by the outer catch
              throw redirectError;
            }
            
            // For other errors, re-throw them
            throw redirectError;
          }
          
          // If we reach here, signInWithRedirect was called and the redirect should be in progress.
          // Don't throw an error - the redirect may be asynchronous.
          // The OAuth callback will handle completion or errors.
          // Note: We intentionally don't set isLoading to false here because the redirect should happen.
        } catch (error: unknown) {
          // Only handle actual configuration errors from signInWithRedirect
          // These are errors that prevent the redirect from happening
          const errorMessage = (error as { message?: string })?.message || '';
          const errorCode = (error as { code?: string })?.code || 'SSO_AUTH_ERROR';
          
          // Only set error state for configuration errors (OAuth, domain, configure)
          // These are errors that prevent the redirect from happening
          if (errorMessage.includes('OAuth') || 
              errorMessage.includes('configure') || 
              errorMessage.includes('domain') ||
              errorMessage.includes('Cognito is not configured')) {
            const appError: AppError = {
              code: errorCode,
              message: errorMessage,
              details: error,
              timestamp: new Date().toISOString(),
            };
            set({
              error: appError,
              isLoading: false,
            });
            throw appError;
          }
          
          // For other errors (like redirect failures), don't set error state
          // The redirect may still happen asynchronously
          // The OAuth callback will handle authentication errors
          console.error('SSO error (non-fatal, redirect may still happen):', error);
          // Don't set error state - redirect may still happen
          // Don't set isLoading to false - redirect is in progress
        }
      },

      loginWithAzureAD: async (username: string, _password: string) => {
        // Note: password parameter is not used because we redirect to Azure AD's login page
        // where the user enters the password. The prompt=login parameter forces credential entry.
        set({ isLoading: true, error: null });
        
        try {
          // Verify Amplify is configured
          if (!amplifyConfig.Auth?.Cognito?.userPoolId || !amplifyConfig.Auth?.Cognito?.userPoolClientId) {
            throw new Error('AWS Cognito is not configured. Please check your environment variables.');
          }

          const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
          if (!currentOrigin) {
            throw new Error('Cannot determine current origin. Authentication requires browser context.');
          }

          // Validate email format (Azure AD uses email as username)
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(username)) {
            throw new Error('Please enter a valid email address for your Azure AD username.');
          }

          // Validate email - reject service account emails
          if (isServiceAccountEmail(username)) {
            const errorMsg = 'Service account logins are not permitted. Please use your standard TNC login email address.';
            set({
              isLoading: false,
              error: {
                code: 'SERVICE_ACCOUNT_NOT_ALLOWED',
                message: errorMsg,
                details: { email: username },
                timestamp: new Date().toISOString(),
              },
            });
            throw new Error(errorMsg);
          }

          // IMPORTANT: We must use Amplify's signInWithRedirect to properly manage PKCE state
          // Manual URL construction bypasses Amplify's state management, causing token exchange failures
          // 
          // Note: Amplify doesn't support passing prompt=login or login_hint directly,
          // but Azure AD will still prompt for credentials when authenticating through Cognito
          
          const cognitoDomain = config.cognitoDomain;
          if (!cognitoDomain) {
            throw new Error('Cognito domain is not configured.');
          }

          // Configure Amplify with correct OAuth settings (same as loginWithSSO)
          const correctedRedirectSignIn = `${currentOrigin}/oauth2/callback`;
          const correctedRedirectSignOut = `${currentOrigin}/login`;
          
          const correctedOAuthConfig = {
            domain: cognitoDomain,
            scopes: ['email', 'openid', 'profile'] as string[],
            redirectSignIn: [correctedRedirectSignIn],
            redirectSignOut: [correctedRedirectSignOut],
            responseType: 'code' as const,
          };
          
          const correctedAmplifyConfig = {
            Auth: {
              Cognito: {
                userPoolId: config.userPoolId,
                userPoolClientId: config.userPoolClientId,
                identityPoolId: config.identityPoolId,
                loginWith: {
                  email: true,
                  username: true,
                  oauth: correctedOAuthConfig,
                },
              },
            },
            Storage: amplifyConfig.Storage ? { ...amplifyConfig.Storage } : undefined,
            API: amplifyConfig.API ? { ...amplifyConfig.API } : undefined,
          } as any;
          
          if (!correctedAmplifyConfig.Storage) { delete correctedAmplifyConfig.Storage; }
          if (!correctedAmplifyConfig.API) { delete correctedAmplifyConfig.API; }
          
          Amplify.configure(correctedAmplifyConfig);
          await new Promise(resolve => setTimeout(resolve, 300));

          // Store username in sessionStorage for verification after callback
          try {
            sessionStorage.setItem('azureAdUsername', username);
          } catch (e) {
            console.warn('Failed to store username in sessionStorage:', e);
          }

          // Use Amplify's signInWithRedirect which properly handles PKCE and state
          // This ensures the authorization code can be exchanged for tokens in the callback
          const providerName = config.cognitoOidcProvider || config.cognitoIdpProvider || 'azuread';
          
          // Pre-sign-out to clear any stale sessions
          try {
            await signOut();
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            // Ignore sign-out errors - proceed with SSO
          }
          
          if (providerName) {
            await signInWithRedirect({ 
              provider: { 
                custom: providerName 
              } as any 
            });
          } else {
            await signInWithRedirect();
          }

          // Note: We don't set isLoading to false here because we're redirecting
          // The OAuth callback will handle the completion
        } catch (error: unknown) {
          const errorMessage = (error as { message?: string })?.message || 'Azure AD authentication failed';
          const errorCode = (error as { code?: string })?.code || 'AZURE_AD_AUTH_ERROR';
          
          const appError: AppError = {
            code: errorCode,
            message: errorMessage,
            details: error,
            timestamp: new Date().toISOString(),
          };
          
          set({ isLoading: false, error: appError });
          throw appError;
        }
      },

      logout: async () => {
        clearIdentitySession();
        try {
          set({ isLoading: true });
          await signOut();
          set({
            user: null,
            authorization: null,
            authorizationStatus: 'pending',
            isAuthenticated: false,
            currentBusinessUnit: null,
            isLoading: false,
            error: null,
          });
        } catch (error: unknown) {
          console.error('Logout error:', error);
          // Force logout even if AWS logout fails
          set({
            user: null,
            authorization: null,
            authorizationStatus: 'pending',
            isAuthenticated: false,
            currentBusinessUnit: null,
            isLoading: false,
            error: null,
          });
        }
      },

      checkAuth: async () => {
        if (identitySettledThisSession) {
          return;
        }
        try {
          set({ isLoading: true });
          
          // Skip auth check if Amplify is not configured
          if (!amplifyConfig.Auth?.Cognito?.userPoolId || !amplifyConfig.Auth?.Cognito?.userPoolClientId) {
            set({
              user: null,
              isAuthenticated: false,
              currentBusinessUnit: null,
              isLoading: false,
              error: null,
            });
            return;
          }
          
          // Attempt to get the current user; if just logged in, this can briefly fail
          let cognitoUser;
          for (let i = 0; i < 5; i++) {
            try {
              cognitoUser = await getCurrentUser();
              if (cognitoUser) {
                console.log(`checkAuth: Got user on attempt ${i + 1}:`, cognitoUser.username);
                break;
              }
            } catch (err: any) {
              console.log(`checkAuth: Attempt ${i + 1}/5 - No user yet:`, err?.message || err);
            }
            if (i < 4) {
              await new Promise(r => setTimeout(r, 300 * (i + 1)));
            }
          }
          
          if (cognitoUser) {
            // Fetch session with retries - forceRefresh is important for OAuth callbacks
            console.log('checkAuth: Fetching auth session...');
            let session = await fetchAuthSession({ forceRefresh: true });
            if (!session.tokens) {
              console.log('checkAuth: No tokens on first attempt, retrying...');
              for (let i = 0; i < 3; i++) {
                await new Promise(r => setTimeout(r, 400));
                session = await fetchAuthSession({ forceRefresh: true });
                if (session.tokens) {
                  console.log(`checkAuth: Got tokens on retry ${i + 1}`);
                  break;
                }
              }
            }
            
            if (!session.tokens) {
              console.error('checkAuth: No tokens available after retries');
              console.error('checkAuth: Session:', session);
              set({
                user: null,
                isAuthenticated: false,
                currentBusinessUnit: null,
                isLoading: false,
                error: {
                  code: 'NO_TOKENS',
                  message: 'Authentication succeeded but tokens are unavailable. Please try logging in again.',
                  details: { username: cognitoUser.username },
                  timestamp: new Date().toISOString(),
                },
              });
              return;
            }
            
            console.log('checkAuth: Got tokens');
            const attributes = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
            
            // Extract user information from ID token attributes
            // For federated users (OIDC/SAML), username is a hash, so we use email/name for display
            const email = (attributes.email as string) || '';
            const name = (attributes.name as string) || '';
            const givenName = (attributes.given_name as string) || '';
            const familyName = (attributes.family_name as string) || '';
            
            // Validate email - reject service account emails
            if (email && isServiceAccountEmail(email)) {
              console.error(`Service account email detected and rejected: ${email}`);
              
              // Sign out the service account - handle errors robustly
              try {
                await signOut();
              } catch (signOutErr) {
                const signOutErrorMsg = extractCognitoErrorMessage(
                  signOutErr,
                  'Failed to sign out service account'
                );
                console.error('Failed to sign out service account:', {
                  error: signOutErr,
                  message: signOutErrorMsg,
                });
                // Continue anyway - validation will still work
              }
              
              const errorMsg = 'Service account logins are not permitted. Please use your standard TNC login email address.';
              const serviceAccountError = {
                code: 'SERVICE_ACCOUNT_NOT_ALLOWED',
                message: errorMsg,
                details: { email },
                timestamp: new Date().toISOString(),
              };
              
              // Persist error to sessionStorage for display after redirect
              try {
                sessionStorage.setItem('serviceAccountError', JSON.stringify(serviceAccountError));
              } catch (e) {
                console.warn('Failed to save service account error to sessionStorage:', e);
              }
              
              set({
                user: null,
                isAuthenticated: false,
                currentBusinessUnit: null,
                isLoading: false,
                error: serviceAccountError,
              });
              return;
            }

            const loaded = await loadAuthorization();
            if (loaded.status === 'unauthenticated') {
              throw sessionExpiredError();
            }
            const authorization = loaded.status === 'ready' ? loaded.authorization : null;
            const authorizationStatus = loaded.status;
            
            // Create display name: prefer name, then givenName + familyName, then email, finally username
            const displayName = name || 
              (givenName && familyName ? `${givenName} ${familyName}` : givenName || familyName) ||
              email ||
              cognitoUser.username;
            
            const defaultBusinessUnit = resolveDefaultBusinessUnit(
              selectableBusinessUnits(authorization),
              get().currentBusinessUnit
            );

            const user: User = {
              id: cognitoUser.username,
              username: cognitoUser.username, // Cognito username (hash for federated users)
              email: email || cognitoUser.username, // Email address (preferred for display)
              displayName: displayName, // Human-readable display name
              businessUnit: defaultBusinessUnit,
              clearanceLevel: 'standard',
              createdAt: new Date().toISOString(),
              lastLogin: new Date().toISOString(),
            };
            
            set({
              user,
              authorization,
              authorizationStatus,
              isAuthenticated: true,
              currentBusinessUnit: defaultBusinessUnit,
              isLoading: false,
              error: null,
            });
          } else {
            set({
              user: null,
              authorization: null,
              isAuthenticated: false,
              currentBusinessUnit: null,
              isLoading: false,
              error: null,
            });
          }
        } catch (error) {
          if ((error as { code?: string }).code === 'SESSION_EXPIRED') {
            clearIdentitySession();
            try {
              await signOut();
            } catch {
              // Still drop the local session so the next paint is the login page.
            }
            set({
              user: null,
              authorization: null,
              authorizationStatus: 'pending',
              isAuthenticated: false,
              currentBusinessUnit: null,
              isLoading: false,
              error: {
                code: 'SESSION_EXPIRED',
                message: 'Your session expired. Sign in again.',
                timestamp: new Date().toISOString(),
              },
            });
            return;
          }
          set({
            user: null,
            authorization: null,
            authorizationStatus: 'pending',
            isAuthenticated: false,
            currentBusinessUnit: null,
            isLoading: false,
            error: null,
          });
        }
      },

      confirmPasswordChange: async (newPassword: string) => {
        try {
          set({ isLoading: true, error: null });
          
          const { passwordResetState } = get();
          if (!passwordResetState || !passwordResetState.isRequired) {
            throw new Error('No password reset in progress');
          }
          
          // Confirm the new password using AWS Amplify
          const result = await confirmSignIn({
            challengeResponse: newPassword,
          });
          
          console.log('Password change confirmed, next step:', result.nextStep);
          
          // If there are more steps required, handle them
          if (result.nextStep && result.nextStep.signInStep !== 'DONE') {
            throw new Error('Password change confirmed but sign-in incomplete. Please try logging in again.');
          }
          
          // Clear the challenge. Keep isLoading true until /identity settles.
          set({ passwordResetState: null });
          
          // Now complete the login process
          const cognitoUser = await getCurrentUser();
          let session = await fetchAuthSession({ forceRefresh: true });
          
          if (!session.tokens) {
            // Try one more time
            await new Promise(resolve => setTimeout(resolve, 300));
            session = await fetchAuthSession({ forceRefresh: true });
          }
          
          if (!session.tokens) {
            throw new Error('Password changed but authentication failed. Please log in again with your new password.');
          }
          
          const attributes = (session.tokens.idToken?.payload || {}) as Record<string, unknown>;

          // Extract user information from ID token attributes
          const email = (attributes.email as string) || '';
          const name = (attributes.name as string) || '';
          const givenName = (attributes.given_name as string) || '';
          const familyName = (attributes.family_name as string) || '';
          
          // Validate email - reject service account emails
          if (email && isServiceAccountEmail(email)) {
            console.warn(`Service account email detected and rejected: ${email}`);
            await signOut(); // Sign out the service account
            throw new Error('Service account logins are not permitted. Please use your standard TNC login email address.');
          }

          const loaded = await loadAuthorization(true);
          if (loaded.status === 'unauthenticated') {
            throw sessionExpiredError();
          }
          const authorization = loaded.status === 'ready' ? loaded.authorization : null;
          const authorizationStatus = loaded.status;
          const defaultBusinessUnit = resolveDefaultBusinessUnit(
            selectableBusinessUnits(authorization),
            get().currentBusinessUnit
          );

          // Create display name: prefer name, then givenName + familyName, then email, finally username
          const displayName = name || 
            (givenName && familyName ? `${givenName} ${familyName}` : givenName || familyName) ||
            email ||
            cognitoUser.username;
          
          const user: User = {
            id: cognitoUser.username,
            username: cognitoUser.username, // Cognito username (hash for federated users)
            email: email || cognitoUser.username, // Email address (preferred for display)
            displayName: displayName, // Human-readable display name
            businessUnit: defaultBusinessUnit,
            clearanceLevel: 'standard',
            createdAt: new Date().toISOString(),
            lastLogin: new Date().toISOString(),
          };
          
          set({
            user,
            authorization,
            authorizationStatus,
            isAuthenticated: true,
            currentBusinessUnit: defaultBusinessUnit,
            isLoading: false,
            error: null,
            passwordResetState: null,
          });
        } catch (error: unknown) {
          const appError: AppError = {
            code: (error as { code?: string })?.code || 'PASSWORD_CHANGE_ERROR',
            message: (error as { message?: string })?.message || 'Failed to change password',
            details: error,
            timestamp: new Date().toISOString(),
          };
          
          set({
            error: appError,
            isLoading: false,
          });
          throw appError;
        }
      },

      setCurrentBusinessUnit: (businessUnitId: string) => {
        const selectedBusinessUnit = selectableBusinessUnits(get().authorization)
          .find((unit) => unit.id === businessUnitId);

        if (!selectedBusinessUnit) {
          console.error(`Business unit ${businessUnitId} is not a selectable unit`);
          return;
        }

        const currentUser = get().user;
        if (currentUser) {
          set({
            user: {
              ...currentUser,
              businessUnit: selectedBusinessUnit,
            },
            currentBusinessUnit: selectedBusinessUnit,
          });
        } else {
          set({
            currentBusinessUnit: selectedBusinessUnit,
          });
        }
      },

      clearPasswordResetState: () => {
        set({ passwordResetState: null });
      },

      retryIdentity: async () => {
        clearIdentitySession();
        set({ isLoading: true, authorizationStatus: 'pending', error: null });
        const loaded = await loadAuthorization(true);
        if (loaded.status === 'unauthenticated') {
          clearIdentitySession();
          try {
            await signOut();
          } catch {
            // Still drop the local session.
          }
          set({
            user: null,
            authorization: null,
            authorizationStatus: 'pending',
            isAuthenticated: false,
            currentBusinessUnit: null,
            isLoading: false,
            error: {
              code: 'SESSION_EXPIRED',
              message: 'Your session expired. Sign in again.',
              timestamp: new Date().toISOString(),
            },
          });
          return;
        }
        const authorization = loaded.status === 'ready' ? loaded.authorization : null;
        const currentUser = get().user;
        const defaultBusinessUnit = resolveDefaultBusinessUnit(
          selectableBusinessUnits(authorization),
          get().currentBusinessUnit
        );
        set({
          authorization,
          authorizationStatus: loaded.status,
          currentBusinessUnit: defaultBusinessUnit,
          isLoading: false,
          user: currentUser ? { ...currentUser, businessUnit: defaultBusinessUnit } : currentUser,
        });
      },

      clearError: () => {
        // Also clear from sessionStorage if it's a service account error
        try {
          sessionStorage.removeItem('serviceAccountError');
        } catch (e) {
          // Ignore sessionStorage errors
        }
        set({ error: null });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        currentBusinessUnit: state.currentBusinessUnit,
      }),
    }
  )
);
